/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observable, runInAction } from "mobx";

import type { Execution } from "@src/@types/Execution";
import type { MinionPool } from "@src/@types/MinionPool";
import type { Schedule } from "@src/@types/Schedule";
import type { TransferItem } from "@src/@types/MainItem";
import TransferSource from "@src/sources/TransferSource";
import MinionPoolSource from "@src/sources/MinionPoolSource";
import ScheduleSource from "@src/sources/ScheduleSource";
import endpointStore from "@src/stores/EndpointStore";
import liveStore from "@src/components/modules/LiveModule/LiveStore";
import { copiedBytes, isActiveStatus, parseApiDate } from "@src/utils/TransferProgress";

import { DAYS, DAY_MS } from "./dashboardData";

/*
 * The slower half of the dashboard's data (the fast half, what is running, is liveStore): each
 * transfer's recent executions, the bytes every finished sync of the last 30 days copied (read once
 * from its tasks, then kept), Swift Agents, and the schedules. Refreshed every 60 s while shown.
 */

const REFRESH_MS = 60 * 1000;
const SCHEDULES_MS = 5 * 60 * 1000;
const MAX_TASK_READS = 150;

async function limited<T>(items: T[], n: number, fn: (item: T) => Promise<void>) {
  const queue = items.slice();
  const workers = Array.from({ length: Math.min(n, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift() as T;
      await fn(item).catch(() => {});
    }
  });
  await Promise.all(workers);
}

class DashboardStore {
  @observable executions: { [transferId: string]: Execution[] } = {};

  @observable copied: { [executionId: string]: number } = {};

  @observable schedules: { [transferId: string]: Schedule[] } = {};

  @observable pools: MinionPool[] = [];

  @observable loaded = false;

  private executionsKey = new Map<string, string>();

  private copiedDone = new Set<string>();

  private schedulesAt = 0;

  private timer: number | null = null;

  private watchers = 0;

  private generation = 0;

  start() {
    this.watchers += 1;
    liveStore.start();
    if (this.watchers === 1) {
      this.loop();
    }
  }

  stop() {
    this.watchers = Math.max(0, this.watchers - 1);
    liveStore.stop();
    if (!this.watchers && this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  reset() {
    this.generation += 1;
    this.executionsKey.clear();
    this.copiedDone.clear();
    this.schedulesAt = 0;
    runInAction(() => {
      this.executions = {};
      this.copied = {};
      this.schedules = {};
      this.pools = [];
      this.loaded = false;
    });
    liveStore.reset();
    if (this.watchers) {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }
      this.loop();
    }
  }

  private async loop() {
    const generation = this.generation;
    try {
      await this.refresh();
    } catch (err) {
      console.error(err);
    }
    if (this.watchers && generation === this.generation) {
      this.timer = window.setTimeout(() => this.loop(), REFRESH_MS);
    }
  }

  private async waitForTransfers() {
    for (let i = 0; i < 100 && !liveStore.loaded; i += 1) {
      await new Promise(r => {
        window.setTimeout(r, 150);
      });
    }
  }

  async refresh() {
    const generation = this.generation;
    const pools = MinionPoolSource.loadMinionPools({ skipLog: true }).catch(() => [] as MinionPool[]);
    if (!endpointStore.endpoints.length && !endpointStore.loading) {
      endpointStore.getEndpoints({ skipLog: true }).catch(() => {});
    }
    await this.waitForTransfers();
    const transfers: TransferItem[] = liveStore.transfers.slice();
    const now = Date.now();

    // Executions: again only for transfers that changed since the last read.
    const executions: { [id: string]: Execution[] } = { ...this.executions };
    const changed = transfers.filter(
      t => this.executionsKey.get(t.id) !== `${t.updated_at}|${t.last_execution_status}` || isActiveStatus(t.last_execution_status),
    );
    await limited(changed, 4, async t => {
      const list = await TransferSource.getExecutions(t.id, {
        limit: 50,
        quietError: true,
        sortKeys: ["number"],
        sortDirs: ["desc"],
      });
      executions[t.id] = list.filter(e => !e.deleted_at);
      this.executionsKey.set(t.id, `${t.updated_at}|${t.last_execution_status}`);
    });
    Object.keys(executions).forEach(id => {
      if (!transfers.find(t => t.id === id)) delete executions[id];
    });

    // Bytes copied by each finished sync of the last 30 days (read once).
    const since = now - (DAYS + 1) * DAY_MS;
    const toRead: { transferId: string; e: Execution }[] = [];
    Object.keys(executions).forEach(transferId => {
      executions[transferId].forEach(e => {
        const end = parseApiDate(e.updated_at) || parseApiDate(e.created_at) || 0;
        if (
          end >= since &&
          (!e.type || e.type === "transfer_execution") &&
          !isActiveStatus(e.status) &&
          !this.copiedDone.has(e.id)
        ) {
          toRead.push({ transferId, e });
        }
      });
    });
    const copied: { [id: string]: number } = { ...this.copied };
    await limited(toRead.slice(0, MAX_TASK_READS), 4, async ({ transferId, e }) => {
      const withTasks = await TransferSource.getExecutionTasks({ transferId, executionId: e.id, polling: true });
      copied[e.id] = copiedBytes(withTasks.tasks);
      this.copiedDone.add(e.id);
    });

    // Schedules (replicas only), every few minutes.
    let schedules = this.schedules;
    if (now - this.schedulesAt > SCHEDULES_MS) {
      schedules = {};
      const replicas = transfers.filter(t => t.scenario !== "live_migration");
      await limited(replicas, 4, async t => {
        schedules[t.id] = await ScheduleSource.getSchedules(t.id, { skipLog: true });
      });
      this.schedulesAt = now;
    }

    const poolList = await pools;
    if (generation !== this.generation) {
      return;
    }
    runInAction(() => {
      this.executions = executions;
      this.copied = copied;
      this.schedules = schedules;
      this.pools = poolList;
      this.loaded = true;
    });
  }
}

export default new DashboardStore();
